import { Controller } from '@hotwired/stimulus'

let FancyboxLib = null

async function loadFancybox() {
  if (FancyboxLib) return FancyboxLib

  const module = await import('@fancyapps/ui')
  FancyboxLib = module.Fancybox || module.default?.Fancybox || module.default

  return FancyboxLib
}

export default class FancyboxController extends Controller {
  async connect() {
    this.Fancybox = await loadFancybox()

    this.Fancybox.unbind(this.element)
    this.Fancybox.bind('[data-fancybox]', {
      Hash: false,
      zoomEffect: false,
      fadeEffect: false,
      showClass: false,
      hideClass: false,
      dragToClose: false,
      Carousel: {
        Video: {
          autoplay: true
        },
        Toolbar: {
          display: {
            left: [],
            middle: [],
            right: ['toggleFull', 'fullscreen', 'close'],
          },
        },
        Zoomable: {
          Panzoom: {
            maxScale: 'cover',
            panMode: 'mousemove',
            mouseMoveFactor: 1.1,
          },
        },
      },
      on: {
        'Carousel.contentLoading': (fb, carousel, slide) => {
          this.showImagePlaceholder(slide)
        },

        'Carousel.contentReady': (fb, carousel, slide) => {
          this.clearImagePlaceholder(slide)

          const trigger = slide.triggerEl

          const feedVideo =
            trigger?.querySelector('video') ||
            trigger?.closest('[data-post]')?.querySelector('video')

          const modalVideo =
            slide.el?.querySelector('.f-html5video video') ||
            slide.el?.querySelector('video')

          if (!feedVideo || !modalVideo) return

          slide.feedVideo = feedVideo
          slide.modalVideo = modalVideo
          slide.originalMuted = feedVideo.muted
          slide.originalVolume = feedVideo.volume
          slide.originalPlaybackRate = feedVideo.playbackRate
          slide.wasPlaying = !feedVideo.paused

          feedVideo.muted = true

          modalVideo.currentTime = feedVideo.currentTime
          modalVideo.volume = feedVideo.volume
          modalVideo.playbackRate = feedVideo.playbackRate
          modalVideo.muted = slide.originalMuted

          if (slide.wasPlaying) {
            modalVideo.play().catch(() => { })
          }
        },

        close: (fb) => {
          const slide = fb.getSlide()

          if (!slide?.feedVideo || !slide?.modalVideo) return

          const { feedVideo, modalVideo } = slide

          feedVideo.currentTime = modalVideo.currentTime
          feedVideo.volume = modalVideo.volume
          feedVideo.playbackRate = modalVideo.playbackRate

          feedVideo.muted = slide.originalMuted

          if (slide.wasPlaying) {
            feedVideo.play().catch(() => { })
          }

          modalVideo.pause()
        }
      }
    })
  }

  showImagePlaceholder(slide) {
    const triggerImage = slide.triggerEl?.querySelector('img')
    const thumbnailSrc = triggerImage?.currentSrc || triggerImage?.src

    if (!slide.el || !thumbnailSrc || slide.triggerEl?.querySelector('video')) return

    const imageWidth = Number(slide.width) ||
      Number(slide.triggerEl.dataset.width) ||
      triggerImage.naturalWidth
    const imageHeight = Number(slide.height) ||
      Number(slide.triggerEl.dataset.height) ||
      triggerImage.naturalHeight
    const fancyboxViewport = slide.el.closest('.fancybox__viewport')
    const frameWidth = slide.el.clientWidth ||
      fancyboxViewport?.clientWidth ||
      globalThis.innerWidth
    const frameHeight = slide.el.clientHeight ||
      fancyboxViewport?.clientHeight ||
      globalThis.innerHeight
    const imageExceedsSlide = imageWidth > frameWidth ||
      imageHeight > frameHeight
    const scale = 1

    slide.el.querySelector('.f-spinner')?.remove()
    slide.el.style.backgroundImage = `url("${thumbnailSrc}")`
    slide.el.style.backgroundPosition = 'center'
    slide.el.style.backgroundRepeat = 'no-repeat'
    slide.el.style.backgroundSize = imageExceedsSlide
      ? 'contain'
      : imageWidth && imageHeight
        ? `${Math.round(imageWidth * scale)}px ${Math.round(imageHeight * scale)}px`
        : 'auto'
    slide.hasImagePlaceholder = true
  }

  clearImagePlaceholder(slide) {
    if (!slide.hasImagePlaceholder || !slide.el) return

    slide.el.style.removeProperty('background-image')
    slide.el.style.removeProperty('background-position')
    slide.el.style.removeProperty('background-repeat')
    slide.el.style.removeProperty('background-size')
    slide.hasImagePlaceholder = false
  }

  disconnect() {
    this.Fancybox?.unbind(this.element)
  }
}
